import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useWindowWidth, buzz } from '../hooks';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const TEAL   = '#14b8a6';
const VIOLET = '#8b5cf6';
const RED    = '#ef4444';
const DARK   = '#0f1117';
const BORD   = '#2a3040';
const MUTED  = '#6b7280';

const RANGES = [
  { key: '1d',  label: 'Past 1 day',      desc: 'last 24 hours' },
  { key: '7d',  label: 'Past 7 days',     desc: 'last 7 days'   },
  { key: '30d', label: 'Past 30 days',    desc: 'last 30 days'  },
  { key: 'all', label: 'Since Beginning', desc: 'all time'      },
];

const KEEP = ['parts', 'products', 'moulds', 'machines', 'materials', 'recipes', 'colours', 'users', 'checklist templates', 'FAQ'];
const WIPE = ['jobs', 'scheduled jobs', 'orders & order lines', 'import snapshots', 'optimisation history', 'weight & scrap logs', 'inspection logs', 'checklist completions'];

const INITIAL_CATS = [
  {
    id: 'production',
    label: 'Production Planning',
    color: TEAL,
    icon: 'fa-solid fa-calendar-days',
    tiles: [
      { id: 'customers', icon: 'fa-solid fa-address-book',  label: 'Customers',       path: '/admin/customers', desc: 'Who you invoice, and the price list', accent: TEAL, need: 'see_money' },
      { id: 'invoices', icon: 'fa-solid fa-file-invoice',    label: 'Invoices',        path: '/admin/invoices', desc: 'Every invoice number, PDFs, paid', accent: TEAL, need: 'see_money' },
      { id: 'closures', icon: 'fa-solid fa-calendar-xmark', label: 'Non-Working Days', path: '/admin/closures', desc: 'Factory shutdowns and custom closures', accent: TEAL },
      { id: 'staffing', icon: 'fa-solid fa-people-group',   label: 'Staffing',         path: '/admin/staffing', desc: 'People for moulding per shift, and attention per part', accent: TEAL, need: 'see_staffing' },
    ],
  },
  {
    id: 'intelligence',
    label: 'Intelligence',
    color: VIOLET,
    icon: 'fa-solid fa-brain',
    tiles: [
      { id: 'insights',   icon: 'fa-solid fa-chart-line', label: 'Insights',   path: '/insights',    desc: 'AI insights and performance overview', accent: VIOLET, need: 'manage' },
      { id: 'measuring',  icon: 'fa-solid fa-stopwatch',  label: 'Measuring',  path: '/admin/measuring', desc: 'Who measures what, and what was measured', accent: VIOLET, need: 'assign_measuring' },
      { id: 'financials', icon: 'fa-solid fa-coins',      label: 'Financials', path: '/financials',  desc: 'Revenue, costs and margin analysis',   accent: VIOLET, need: 'see_money' },
    ],
  },
  {
    id: 'catalogues',
    label: 'Catalogues',
    color: '#4b5563',
    icon: 'fa-solid fa-layer-group',
    tiles: [
      { id: 'moulds',    icon: 'fa-solid fa-box',      label: 'Moulds',      path: '/admin/moulds',    desc: 'Mould catalogue and drawings',          accent: '#9ca3af' },
      { id: 'machines',  icon: 'fa-solid fa-industry', label: 'Machines',    path: '/admin/machines',  desc: 'Machine settings and checklists',       accent: '#9ca3af' },
      { id: 'materials', icon: 'fa-solid fa-flask',    label: 'Materials',   path: '/admin/materials', desc: 'Materials, recipes and additives',      accent: '#9ca3af' },
      { id: 'packaging', icon: 'fa-solid fa-box-open', label: 'Packaging',   path: '/admin/packaging', desc: 'Boxes, pallets, foil, tape, and how each item is packed', accent: '#9ca3af', need: 'see_packaging' },
      { id: 'bulk',      icon: 'fa-solid fa-list-check', label: 'Bulk Tool', path: '/admin/bulk',      desc: 'Edit many items one after another',     accent: '#9ca3af', need: 'manage' },
    ],
  },
  {
    id: 'system',
    label: 'System',
    color: '#4b5563',
    icon: 'fa-solid fa-gear',
    tiles: [
      { id: 'users',    icon: 'fa-solid fa-users',         label: 'Users',       path: '/admin/users',    desc: 'User management and roles',                             accent: '#9ca3af', need: 'manage' },
      // Suggest a change (28 Oct 2026): what operators and setters send from the bar.
      { id: 'suggestions', icon: 'fa-solid fa-lightbulb',  label: 'Suggestions', path: '/admin/suggestions', desc: 'Changes the floor suggests, with a picture of the page', accent: TEAL, need: 'manage' },
      { id: 'upload',   icon: 'fa-solid fa-file-arrow-up', label: 'Mass Upload', path: '/admin/import',   desc: 'Bulk import from any file',                             accent: '#9ca3af', need: 'manage' },
      { id: 'data',     icon: 'fa-solid fa-right-left',    label: 'Export & Import', path: '/admin/data', desc: 'Back up your data, or set up a fresh install',        accent: TEAL, need: 'manage' },
      // Was Archive (until 1 Oct 2026); the id stays so a saved tile order keeps its place.
      { id: 'archive',  icon: 'fa-solid fa-clock-rotate-left', label: 'History', path: '/admin/history', desc: 'Every change, and everything no longer in use', accent: '#f59e0b', need: 'manage' },
      { id: 'settings', icon: 'fa-solid fa-sliders',       label: 'Settings',    path: '/admin/settings', desc: 'App preferences and device controls',                   accent: '#9ca3af', need: 'manage' },
      { id: 'faq',      icon: 'fa-solid fa-circle-question', label: 'FAQ',       path: '/faq',            desc: 'Questions and answers',                                 accent: '#9ca3af' },
    ],
  },
];

// Returns grid config for a given tile count and breakpoint.
// Even counts → all tiles equal. Odd counts → first tile spans full width (featured).
// Desktop: 2 cols (≤5 tiles), 3 cols (6–7), 4 cols (8). Tablet: max 2 cols. Mobile: 1 col.
function getTileGrid(count, bp) {
  if (bp === 'mobile' || count <= 1) {
    return { cols: '1fr', spans: Array(Math.max(1, count)).fill(false) };
  }
  const odd   = count % 2 !== 0;
  const spans = Array(count).fill(false);
  if (odd) spans[0] = true;
  if (bp === 'tablet' || count <= 5) return { cols: '1fr 1fr', spans };
  if (count <= 7)                    return { cols: '1fr 1fr 1fr', spans };
  return { cols: '1fr 1fr 1fr 1fr', spans: Array(count).fill(false) };
}

function loadLS(key, fallback) {
  try { const v = localStorage.getItem(key); if (v !== null) return JSON.parse(v); } catch {}
  return fallback;
}
function saveLS(key, v) { try { localStorage.setItem(key, JSON.stringify(v)); } catch {} }

function reorder(arr, fromIdx, toIdx) {
  const next = [...arr];
  const [item] = next.splice(fromIdx, 1);
  next.splice(toIdx, 0, item);
  return next;
}

export default function Admin() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const director = can('manage');
  const windowWidth = useWindowWidth();
  const tileIsForMe = (t) => (!t.need || can(t.need)) && (t.id !== 'faq' || windowWidth < 900);

  // Persisted layout state
  const [catOrder, setCatOrder]       = useState(() => loadLS('admin_cat_order',   INITIAL_CATS.map(c => c.id)));
  const [tileOrders, setTileOrders]   = useState(() => loadLS('admin_tile_orders', {}));
  const [collapsed, setCollapsed]     = useState(() => loadLS('admin_collapsed',   {}));

  // DnD state
  const [dragCat,  setDragCat]  = useState(null);  // catId
  const [overCat,  setOverCat]  = useState(null);  // catId
  const [dragTile, setDragTile] = useState(null);  // { catId, tileId }
  const [overTile, setOverTile] = useState(null);  // { catId, tileId }

  // Hover state for tiles
  const [hoveredTile, setHoveredTile] = useState(null); // `${catId}:${tileId}`

  // Reset modal
  const [resetOpen,  setResetOpen]  = useState(false);
  const [resetRange, setResetRange] = useState(null);
  const [resetInput, setResetInput] = useState('');
  const [resetting,  setResetting]  = useState(false);
  const [resetDone,  setResetDone]  = useState(null);
  const [resetError, setResetError] = useState(null);

  // ── Computed ──────────────────────────────────────────────────────────────────
  const catMap = Object.fromEntries(INITIAL_CATS.map(c => [c.id, c]));
  // Only what this role gets, and no heading with nothing under it.
  const orderedCats = catOrder.map(id => catMap[id]).filter(Boolean).filter(c => c.tiles.some(tileIsForMe));

  function orderedTilesFor(cat) {
    const order = tileOrders[cat.id];
    const mine = cat.tiles.filter(tileIsForMe);
    if (!order) return mine;
    const map = Object.fromEntries(mine.map(t => [t.id, t]));
    // A tile added since the order was saved goes at the end, rather than
    // being left out because the saved order never heard of it.
    const placed = order.map(id => map[id]).filter(Boolean);
    return [...placed, ...mine.filter(t => !order.includes(t.id))];
  }

  // ── Category DnD ─────────────────────────────────────────────────────────────
  function onCatDragStart(e, catId) {
    setDragCat(catId);
    e.dataTransfer.effectAllowed = 'move';
  }
  function onCatDragOver(e, catId) {
    e.preventDefault();
    if (!dragCat || dragCat === catId) return;
    setOverCat(catId);
  }
  function onCatDrop(e, catId) {
    e.preventDefault();
    if (!dragCat || catId === dragCat) { setDragCat(null); setOverCat(null); return; }
    const from = catOrder.indexOf(dragCat);
    const to   = catOrder.indexOf(catId);
    const next = reorder(catOrder, from, to);
    setCatOrder(next);
    saveLS('admin_cat_order', next);
    setDragCat(null); setOverCat(null);
  }
  function onCatDragEnd() { setDragCat(null); setOverCat(null); }

  // ── Tile DnD ─────────────────────────────────────────────────────────────────
  function onTileDragStart(e, catId, tileId) {
    e.stopPropagation();
    setDragTile({ catId, tileId });
    e.dataTransfer.effectAllowed = 'move';
  }
  function onTileDragOver(e, catId, tileId) {
    e.preventDefault();
    e.stopPropagation();
    if (!dragTile || dragTile.catId !== catId || tileId === dragTile.tileId) return;
    setOverTile({ catId, tileId });
  }
  function onTileDrop(e, catId, tileId) {
    e.preventDefault();
    e.stopPropagation();
    if (!dragTile || dragTile.catId !== catId || tileId === dragTile.tileId) {
      setDragTile(null); setOverTile(null); return;
    }
    const cat     = catMap[catId];
    const current = tileOrders[catId] || cat.tiles.map(t => t.id);
    const from    = current.indexOf(dragTile.tileId);
    const to      = current.indexOf(tileId);
    const next    = reorder(current, from, to);
    const nextOrders = { ...tileOrders, [catId]: next };
    setTileOrders(nextOrders);
    saveLS('admin_tile_orders', nextOrders);
    buzz(40);
    setDragTile(null); setOverTile(null);
  }
  function onTileDragEnd(e) { e.stopPropagation(); setDragTile(null); setOverTile(null); }

  // ── Collapse toggle ───────────────────────────────────────────────────────────
  function toggleCollapse(catId, e) {
    e.stopPropagation();
    const next = { ...collapsed, [catId]: !collapsed[catId] };
    setCollapsed(next);
    saveLS('admin_collapsed', next);
  }

  // ── Reset modal ───────────────────────────────────────────────────────────────
  function openReset(range) {
    setResetRange(range); setResetInput('');
    setResetDone(null);   setResetError(null);
    setResetOpen(true);
  }
  function closeReset() { if (!resetting) setResetOpen(false); }

  async function handleReset() {
    if (resetInput.trim() !== 'RESET' || !resetRange) return;
    setResetting(true); setResetError(null);
    try {
      const res = await api.post('/api/v1/admin/reset', { confirm: 'RESET', range: resetRange.key });
      setResetDone(res.data);
    } catch (err) {
      setResetError(err.response?.data?.error || err.message || 'Reset failed');
    } finally { setResetting(false); }
  }

  const canReset = resetInput.trim() === 'RESET';
  const isDraggingAnything = !!(dragCat || dragTile);

  const bp = windowWidth < 600 ? 'mobile' : windowWidth < 1024 ? 'tablet' : 'desktop';

  return (
    <div style={{
      padding: '24px', paddingBottom: '84px', fontFamily: 'system-ui, sans-serif',
      color: '#f0f2f5', background: DARK, minHeight: 'calc(100vh - 48px)',
      boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
    }}>
      {/* ── Header ── */}
      <h1 style={{ fontSize: '26px', fontWeight: '700', marginBottom: '4px' }}>{director ? 'Admin' : 'More'}</h1>
      <p style={{ color: MUTED, marginBottom: '24px', fontSize: '14px' }}>{director ? 'Manage your factory' : 'The rest of what you can see'}</p>

      {/* ── Content ── */}
      <div style={{ maxWidth: '1100px', width: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', flex: 1, gap: '16px' }}>

        {/* ── Categories grid ── */}
        <div style={{ display: 'grid', gridTemplateColumns: bp === 'mobile' ? '1fr' : '1fr 1fr', gap: '16px' }}>
          {orderedCats.map(cat => {
            const isDraggingThis = dragCat === cat.id;
            const isDropTarget   = overCat === cat.id && dragCat && dragCat !== cat.id;
            const isCollapsed    = collapsed[cat.id];
            const tiles          = orderedTilesFor(cat);

            return (
              <div
                key={cat.id}
                draggable
                onDragStart={e => onCatDragStart(e, cat.id)}
                onDragOver={e => onCatDragOver(e, cat.id)}
                onDrop={e => onCatDrop(e, cat.id)}
                onDragEnd={onCatDragEnd}
                style={{
                  border: `1px solid ${isDropTarget ? cat.color + '99' : cat.color + '44'}`,
                  borderRadius: '18px',
                  padding: '22px',
                  background: isDropTarget ? `${cat.color}18` : `${cat.color}0a`,
                  display: 'flex', flexDirection: 'column', gap: '14px',
                  opacity: isDraggingThis ? 0.35 : 1,
                  transition: 'opacity 0.15s, border-color 0.15s, background 0.15s',
                  boxSizing: 'border-box',
                }}
              >
                {/* Category header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'grab', pointerEvents: isDraggingThis ? 'none' : 'auto' }}>
                  <i className="fa-solid fa-grip-vertical"
                    style={{ fontSize: '11px', color: MUTED, opacity: 0.45, flexShrink: 0 }} />
                  <i className={cat.icon} style={{ fontSize: '14px', color: `${cat.color}cc`, flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', fontWeight: '700', color: `${cat.color}cc`,
                    textTransform: 'uppercase', letterSpacing: '0.07em', flex: 1 }}>
                    {cat.label}
                  </span>
                  <button
                    onClick={e => toggleCollapse(cat.id, e)}
                    title={isCollapsed ? 'Expand' : 'Collapse'}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
                      color: MUTED, fontSize: '11px', opacity: 0.6, flexShrink: 0,
                      display: 'flex', alignItems: 'center' }}>
                    <i className={`fa-solid fa-chevron-${isCollapsed ? 'down' : 'up'}`} />
                  </button>
                </div>

                {/* Tile grid */}
                {!isCollapsed && (
                  <div
                    style={{ display: 'grid', gridTemplateColumns: getTileGrid(tiles.length, bp).cols,
                      gap: '12px', flex: 1, pointerEvents: isDraggingThis ? 'none' : 'auto' }}
                  >
                    {tiles.map((tile, idx) => {
                      const isDraggingTile = dragTile?.tileId === tile.id && dragTile?.catId === cat.id;
                      const isOverTile     = overTile?.tileId === tile.id && overTile?.catId === cat.id;
                      const isHovered      = hoveredTile === `${cat.id}:${tile.id}` && !isDraggingAnything;
                      const spans          = getTileGrid(tiles.length, bp).spans[idx];

                      return (
                        <div
                          key={tile.id}
                          draggable
                          onDragStart={e => onTileDragStart(e, cat.id, tile.id)}
                          onDragOver={e => onTileDragOver(e, cat.id, tile.id)}
                          onDrop={e => onTileDrop(e, cat.id, tile.id)}
                          onDragEnd={onTileDragEnd}
                          onClick={() => { buzz(); navigate(tile.path); }}
                          onMouseEnter={() => setHoveredTile(`${cat.id}:${tile.id}`)}
                          onMouseLeave={() => setHoveredTile(null)}
                          style={{
                            gridColumn: spans ? '1 / -1' : undefined,
                            background: (isHovered || isOverTile) ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.03)',
                            border: `1px solid ${isOverTile ? tile.accent + '88' : isHovered ? tile.accent + '44' : 'rgba(255,255,255,0.08)'}`,
                            borderRadius: '12px', padding: '20px',
                            cursor: isDraggingAnything ? 'grabbing' : 'pointer',
                            opacity: isDraggingTile ? 0.35 : 1,
                            transition: 'background 0.15s, border-color 0.15s, opacity 0.15s',
                            display: 'flex', flexDirection: 'column',
                            userSelect: 'none',
                          }}
                        >
                          <i className={tile.icon}
                            style={{ fontSize: '24px', color: tile.accent, marginBottom: '12px', pointerEvents: 'none' }} />
                          <div style={{ fontSize: '15px', fontWeight: '700', marginBottom: '4px', color: '#f0f2f5', pointerEvents: 'none' }}>
                            {tile.label}
                          </div>
                          <div style={{ fontSize: '12px', color: MUTED, lineHeight: '1.4', pointerEvents: 'none' }}>
                            {tile.desc}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Spacer ── */}
        <div style={{ flex: 1, minHeight: '16px' }} />

        {/* ── Danger Zone ── */}
        {director && <div style={{ border: `1px solid ${RED}55`, borderRadius: '12px', padding: '20px', background: `${RED}28` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <i className="fa-solid fa-triangle-exclamation" style={{ color: RED, fontSize: '14px' }} />
            <span style={{ fontSize: '13px', fontWeight: '700', color: RED, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Danger Zone</span>
          </div>
          <p style={{ fontSize: '12px', color: MUTED, margin: '0 0 16px' }}>
            These actions are irreversible. Catalogue data is preserved.
          </p>
          <div style={{ background: '#2a0a0a', border: `1px solid ${RED}33`, borderRadius: '8px', padding: '14px 16px' }}>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '2px' }}>Reset all operational data</div>
              <div style={{ fontSize: '11px', color: MUTED }}>
                Permanently deletes all jobs, orders, schedules, and production history. Financials will clear.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {RANGES.map(r => (
                <button key={r.key} onClick={() => openReset(r)}
                  style={{ background: 'transparent', border: `1px solid ${RED}55`, borderRadius: '6px',
                    color: RED, fontSize: '12px', fontWeight: '600', padding: '5px 12px', cursor: 'pointer',
                    transition: 'background 0.12s, border-color 0.12s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = `${RED}22`; e.currentTarget.style.borderColor = RED; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = `${RED}55`; }}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>}
      </div>

      {/* ── Reset modal ── */}
      {resetOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={closeReset}>
          <div style={{ background: '#161b22', border: `1px solid ${RED}44`, borderRadius: '14px',
            padding: '28px', width: '460px', maxWidth: '90vw' }}
            onClick={e => e.stopPropagation()}>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
              <i className="fa-solid fa-skull" style={{ color: RED, fontSize: '18px' }} />
              <span style={{ fontSize: '16px', fontWeight: '700', color: '#f0f2f5' }}>Reset operational data</span>
            </div>
            <div style={{ fontSize: '12px', color: MUTED, marginBottom: '18px' }}>
              Scope: <span style={{ color: '#fbbf24', fontWeight: '600' }}>{resetRange?.label}</span>
            </div>

            {resetDone ? (
              <div>
                <div style={{ background: '#0d2010', border: '1px solid #16a34a44', borderRadius: '8px',
                  padding: '14px', marginBottom: '18px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#4ade80', marginBottom: '8px' }}>
                    <i className="fa-solid fa-circle-check" style={{ marginRight: '6px' }} />
                    Reset complete — {resetRange?.desc}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 16px' }}>
                    {Object.entries(resetDone.deleted).map(([key, n]) => (
                      <div key={key} style={{ fontSize: '11px', color: MUTED }}>
                        <span style={{ color: '#f0f2f5', fontWeight: '600' }}>{n}</span> {key.replace(/_/g, ' ')} deleted
                      </div>
                    ))}
                  </div>
                </div>
                <button onClick={() => setResetOpen(false)}
                  style={{ width: '100%', background: '#16a34a', border: 'none', borderRadius: '8px',
                    color: '#fff', fontWeight: '600', fontSize: '13px', padding: '10px', cursor: 'pointer' }}>
                  Close
                </button>
              </div>
            ) : (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '18px' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: RED, fontWeight: '700', marginBottom: '5px',
                      textTransform: 'uppercase', letterSpacing: '0.05em' }}>Will be deleted</div>
                    {WIPE.map(w => (
                      <div key={w} style={{ fontSize: '11px', color: '#f87171', marginBottom: '2px' }}>
                        <i className="fa-solid fa-xmark" style={{ marginRight: '5px', width: '10px' }} />{w}
                      </div>
                    ))}
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: TEAL, fontWeight: '700', marginBottom: '5px',
                      textTransform: 'uppercase', letterSpacing: '0.05em' }}>Kept</div>
                    {KEEP.map(k => (
                      <div key={k} style={{ fontSize: '11px', color: '#6ee7b7', marginBottom: '2px' }}>
                        <i className="fa-solid fa-check" style={{ marginRight: '5px', width: '10px' }} />{k}
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ background: `${RED}0d`, border: `1px solid ${RED}33`, borderRadius: '8px',
                  padding: '12px 14px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', color: '#f87171', marginBottom: '8px' }}>
                    Type <strong>RESET</strong> to confirm. This cannot be undone.
                  </div>
                  <input type="text" value={resetInput}
                    onChange={e => setResetInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && canReset) handleReset(); }}
                    placeholder="Type RESET" autoFocus
                    style={{ width: '100%', background: '#0f1117', border: `1px solid ${canReset ? RED : BORD}`,
                      borderRadius: '6px', color: canReset ? '#f87171' : '#f0f2f5', fontSize: '13px',
                      padding: '8px 10px', outline: 'none', boxSizing: 'border-box',
                      fontFamily: 'monospace', letterSpacing: '0.1em' }} />
                </div>
                {resetError && (
                  <div style={{ fontSize: '12px', color: '#f87171', marginBottom: '12px', background: `${RED}10`,
                    border: `1px solid ${RED}33`, borderRadius: '6px', padding: '8px 12px' }}>
                    <i className="fa-solid fa-circle-xmark" style={{ marginRight: '6px' }} />{resetError}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={closeReset} disabled={resetting}
                    style={{ flex: 1, background: 'transparent', border: `1px solid ${BORD}`, borderRadius: '8px',
                      color: MUTED, fontSize: '13px', padding: '9px', cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button onClick={handleReset} disabled={!canReset || resetting}
                    style={{ flex: 1, background: canReset && !resetting ? RED : `${RED}44`,
                      border: 'none', borderRadius: '8px', color: canReset && !resetting ? '#fff' : '#6b7280',
                      fontSize: '13px', fontWeight: '700', padding: '9px',
                      cursor: canReset && !resetting ? 'pointer' : 'not-allowed', transition: 'background 0.15s' }}>
                    {resetting
                      ? <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '6px' }} />Resetting…</>
                      : `Reset — ${resetRange?.label}`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
